// patientMonitoringGuide.js
// Builds the one-page clinic-branded enrollment sheet handed to owners.
// The dashboard supplies the existing enrollment QR canvas and clinic data;
// this module owns only the print/PDF composition.

import { jsPDF } from 'jspdf';
import { c } from './dashboard/constants';

const PAGE_WIDTH = 8.5;
const PAGE_HEIGHT = 11;
const MARGIN = 0.38;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const LOGO_ASPECT = 150 / 560;

const COLORS = {
  white: c.white,
  fog: c.fog,
  slate: c.slate,
  slateDeep: c.slateDeep,
  moss: c.moss,
  greenLight: c.greenLight,
  sandstoneLight: c.sandstoneLight,
  textSoft: c.textSoft,
  border: c.border,
};

export function guideDownloadCopy(hasStoreLinks) {
  return hasStoreLinks
    ? {
        body: 'Search “Cairn Pet Health” on the App Store or Google Play.',
        firstStep: 'Download Cairn',
      }
    : {
        body: 'Your veterinary team will provide the current Cairn download link before monitoring begins.',
        firstStep: 'Download Cairn',
      };
}

export function sanitizeFilename(clinicName) {
  const base = (clinicName || 'Clinic')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'Clinic';
  return `Cairn-Patient-Monitoring-Guide-${base}.pdf`;
}

export function formatPhoneForDisplay(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

// jsPDF cannot embed arbitrary SVG markup. Rasterize the approved source at
// 3x display dimensions so its shape and color remain crisp on office printers.
export function svgToPngDataUrl(svgMarkup, displayWidthPx, displayHeightPx, scale = 3) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = displayWidthPx * scale;
      canvas.height = displayHeightPx * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to rasterize Cairn artwork'));
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgMarkup)))}`;
  });
}

export async function svgUrlToPngDataUrl(svgUrl, displayWidthPx, displayHeightPx, scale = 3) {
  const response = await fetch(svgUrl);
  if (!response.ok) throw new Error('Failed to load Cairn artwork');
  return svgToPngDataUrl(await response.text(), displayWidthPx, displayHeightPx, scale);
}

// Convert a bundled raster asset to a data URL before handing it to jsPDF.
// The Google Play badge is supplied by Google as a PNG (including its required
// clear-space padding), so it should not go through the SVG-only loader above.
export function imageUrlToPngDataUrl(imageUrl, displayWidthPx, displayHeightPx, scale = 1) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = displayWidthPx * scale;
      canvas.height = displayHeightPx * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load store badge artwork'));
    img.src = imageUrl;
  });
}

function setText(doc, size, color = COLORS.slate, style = 'normal') {
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
  doc.setTextColor(color);
}

function wrapped(doc, text, x, y, width, lineHeight) {
  const lines = doc.splitTextToSize(text, width);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function drawCheckInIcon(doc, x, y) {
  doc.setDrawColor(COLORS.moss);
  doc.setLineWidth(0.035);
  doc.roundedRect(x, y, 0.42, 0.49, 0.05, 0.05, 'S');
  [0.13, 0.24, 0.35].forEach(offset => doc.line(x + 0.11, y + offset, x + 0.31, y + offset));
}

function drawTrendIcon(doc, x, y) {
  doc.setDrawColor(COLORS.moss);
  doc.setLineWidth(0.035);
  const points = [[x, y + 0.38], [x + 0.17, y + 0.20], [x + 0.36, y + 0.31], [x + 0.57, y + 0.04]];
  for (let i = 0; i < points.length - 1; i += 1) doc.line(...points[i], ...points[i + 1]);
  points.forEach(([px, py]) => doc.circle(px, py, 0.045, 'S'));
}

function drawClinicIcon(doc, x, y) {
  doc.setDrawColor(COLORS.moss);
  doc.setLineWidth(0.035);
  doc.circle(x + 0.34, y + 0.11, 0.11, 'S');
  doc.circle(x + 0.08, y + 0.18, 0.075, 'S');
  doc.lines([[0.11, -0.13], [0.15, 0], [0.11, 0.13]], x - 0.02, y + 0.44, [1, 1], 'S');
  doc.lines([[0.15, -0.19], [0.23, 0], [0.15, 0.19]], x + 0.19, y + 0.49, [1, 1], 'S');
}

function drawStepNumber(doc, number, x, y) {
  doc.setFillColor(COLORS.moss);
  doc.circle(x, y, 0.19, 'F');
  setText(doc, 12, COLORS.white, 'bold');
  doc.text(String(number), x, y + 0.045, { align: 'center' });
}

function drawPhone(doc, x, y, markDataUrl) {
  doc.setDrawColor(COLORS.slate);
  doc.setLineWidth(0.03);
  doc.roundedRect(x, y, 0.42, 0.65, 0.05, 0.05, 'S');
  doc.line(x + 0.15, y + 0.06, x + 0.27, y + 0.06);
  doc.line(x + 0.17, y + 0.59, x + 0.25, y + 0.59);
  if (markDataUrl) doc.addImage(markDataUrl, 'PNG', x + 0.12, y + 0.22, 0.19, 0.19);
}

function drawAccountIcon(doc, x, y) {
  doc.setDrawColor(COLORS.slate);
  doc.setLineWidth(0.03);
  doc.circle(x + 0.20, y + 0.13, 0.095, 'S');
  doc.lines([[0.15, -0.16], [0.20, 0], [0.15, 0.16]], x + 0.05, y + 0.48, [1, 1], 'S');
  doc.setFillColor(COLORS.moss);
  doc.circle(x + 0.43, y + 0.39, 0.11, 'F');
  doc.setDrawColor(COLORS.white);
  doc.setLineWidth(0.02);
  doc.line(x + 0.37, y + 0.39, x + 0.49, y + 0.39);
  doc.line(x + 0.43, y + 0.33, x + 0.43, y + 0.45);
}

function drawScanIcon(doc, x, y) {
  doc.setDrawColor(COLORS.slate);
  doc.setLineWidth(0.035);
  doc.lines([[0, -0.12], [0.12, 0]], x, y + 0.12, [1, 1], 'S');
  doc.lines([[0.12, 0], [0, 0.12]], x + 0.43, y, [1, 1], 'S');
  doc.lines([[0, 0.12], [0.12, 0]], x, y + 0.43, [1, 1], 'S');
  doc.lines([[-0.12, 0], [0, -0.12]], x + 0.55, y + 0.43, [1, 1], 'S');
  doc.rect(x + 0.20, y + 0.20, 0.16, 0.16, 'S');
}

function drawStoreBadge(doc, badgeDataUrl, url, x, y, width, height) {
  if (!badgeDataUrl || !url) return;
  doc.addImage(badgeDataUrl, 'PNG', x, y, width, height, undefined, 'FAST');
  doc.link(x, y, width, height, { url });
}

function drawClinicIdentity(doc, clinicName, clinicLogoDataUrl) {
  const x = 4.72;
  const y = 0.28;
  const width = 3.40;
  const height = 0.83;
  doc.setFillColor(COLORS.greenLight);
  doc.roundedRect(x, y, width, height, 0.12, 0.12, 'F');

  const logoWidth = clinicLogoDataUrl ? 0.54 : 0;
  if (clinicLogoDataUrl) doc.addImage(clinicLogoDataUrl, 'PNG', x + 0.18, y + 0.15, logoWidth, 0.53);
  const textX = x + (clinicLogoDataUrl ? 0.84 : 0.18);
  const textWidth = width - (clinicLogoDataUrl ? 1.02 : 0.36);
  setText(doc, 20, COLORS.slateDeep, 'bold');
  let lines = doc.splitTextToSize(clinicName, textWidth);
  if (lines.length > 2) {
    setText(doc, 15, COLORS.slateDeep, 'bold');
    lines = doc.splitTextToSize(clinicName, textWidth);
  }
  if (lines.length > 3) {
    setText(doc, 12.5, COLORS.slateDeep, 'bold');
    lines = doc.splitTextToSize(clinicName, textWidth);
  }
  const lineHeight = lines.length > 3 ? 0.17 : lines.length > 2 ? 0.20 : 0.27;
  const firstY = y + height / 2 - ((lines.length - 1) * lineHeight) / 2 + 0.07;
  doc.text(lines, textX + textWidth / 2, firstY, { align: 'center' });
}

export function buildPatientMonitoringGuideDoc({
  clinicName,
  enrollmentCode,
  logoDataUrl,
  markDataUrl,
  petOutlineDataUrl,
  clinicLogoDataUrl,
  enrollmentQrDataUrl,
  appStoreUrl,
  googlePlayUrl,
  appStoreBadgeDataUrl,
  googlePlayBadgeDataUrl,
}) {
  if (!enrollmentQrDataUrl) throw new Error('Missing enrollment QR code — cannot generate the guide.');
  if (!clinicName) throw new Error('Missing clinic name — cannot generate the guide.');

  const doc = new jsPDF({ unit: 'in', format: 'letter', orientation: 'portrait', compress: true });
  doc.setLineHeightFactor(1.16);

  // Header: official Cairn wordmark remains subordinate to clinic identity.
  if (logoDataUrl) doc.addImage(logoDataUrl, 'PNG', MARGIN + 0.04, 0.35, 1.82, 1.82 * LOGO_ASPECT);
  drawClinicIdentity(doc, clinicName, clinicLogoDataUrl);

  // Hero.
  setText(doc, 32, COLORS.slateDeep, 'bold');
  doc.text(['Keep the story going', 'between visits.'], MARGIN + 0.02, 1.62);
  setText(doc, 13.2, COLORS.slate, 'normal');
  wrapped(
    doc,
    'Cairn helps you record how your pet is doing over time, giving you and your veterinary team a clearer picture between visits.',
    MARGIN + 0.02,
    2.51,
    5.55,
    0.22
  );
  if (petOutlineDataUrl) doc.addImage(petOutlineDataUrl, 'PNG', 6.47, 1.74, 1.45, 0.91);

  // Three concepts.
  const conceptY = 3.36;
  const colWidth = CONTENT_WIDTH / 3;
  doc.setDrawColor(COLORS.border);
  doc.setLineWidth(0.012);
  doc.line(MARGIN + colWidth, conceptY + 0.02, MARGIN + colWidth, 4.82);
  doc.line(MARGIN + colWidth * 2, conceptY + 0.02, MARGIN + colWidth * 2, 4.82);

  const concepts = [
    {
      title: 'CHECK IN',
      body: 'Brief questions about how your pet has been doing.',
      icon: x => drawCheckInIcon(doc, x + 0.05, conceptY),
    },
    {
      title: 'TRACK OVER TIME',
      body: 'Follow changes in weight, medications, symptoms, and daily wellbeing.',
      icon: x => drawTrendIcon(doc, x + 0.05, conceptY + 0.03),
    },
    {
      title: 'KEEP YOUR CLINIC INFORMED',
      body: 'Your veterinary team can review meaningful changes over time.',
      icon: x => drawClinicIcon(doc, x + 0.05, conceptY),
    },
  ];
  concepts.forEach((concept, i) => {
    const x = MARGIN + i * colWidth + 0.16;
    concept.icon(x);
    setText(doc, 12.2, COLORS.slateDeep, 'bold');
    const titleY = concept.title.length > 20 ? 4.08 : 4.13;
    const titleLines = doc.splitTextToSize(concept.title, colWidth - 0.30);
    doc.text(titleLines, x, titleY);
    setText(doc, 10.1, COLORS.slate, 'normal');
    const bodyY = titleY + titleLines.length * 0.18 + 0.10;
    wrapped(doc, concept.body, x, bodyY, colWidth - 0.32, 0.17);
  });

  doc.setDrawColor(COLORS.moss);
  doc.setLineWidth(0.02);
  doc.line(MARGIN, 5.08, PAGE_WIDTH - MARGIN, 5.08);

  // Enrollment steps, left.
  setText(doc, 27, COLORS.slateDeep, 'bold');
  doc.text('Connect your pet', MARGIN + 0.02, 5.56);
  setText(doc, 11.5, COLORS.slate, 'normal');
  wrapped(doc, `Follow these three steps to connect your pet with ${clinicName}.`, MARGIN + 0.02, 5.90, 4.25, 0.19);

  const hasStoreLinks = Boolean(appStoreUrl || googlePlayUrl);
  const downloadCopy = guideDownloadCopy(hasStoreLinks);
  const stepX = MARGIN + 0.22;
  const iconX = MARGIN + 0.78;
  const textX = MARGIN + 1.50;

  drawStepNumber(doc, 1, stepX, 6.65);
  drawPhone(doc, iconX, 6.32, markDataUrl);
  setText(doc, 12.3, COLORS.slateDeep, 'bold');
  doc.text(downloadCopy.firstStep, textX, 6.52);
  setText(doc, 9.8, COLORS.slate, 'normal');
  wrapped(doc, downloadCopy.body, textX, 6.76, 2.77, 0.16);
  doc.setFillColor(COLORS.greenLight);
  doc.roundedRect(textX, 7.14, 1.66, 0.28, 0.07, 0.07, 'F');
  setText(doc, 9.6, COLORS.slate, 'bold');
  doc.text('Free for pet owners', textX + 0.83, 7.33, { align: 'center' });

  drawStepNumber(doc, 2, stepX, 7.80);
  drawAccountIcon(doc, iconX, 7.49);
  setText(doc, 12.1, COLORS.slateDeep, 'bold');
  doc.text(['Create your account', 'and add your pet.'], textX, 7.70);

  drawStepNumber(doc, 3, stepX, 8.73);
  drawScanIcon(doc, iconX + 0.01, 8.45);
  setText(doc, 12.1, COLORS.slateDeep, 'bold');
  const stepThreeTitleEnd = wrapped(doc, `Link to ${clinicName}`, textX, 8.59, 2.70, 0.18);
  setText(doc, 9.8, COLORS.slate, 'normal');
  wrapped(doc, 'Open Cairn and scan the QR code, or enter the clinic code manually.', textX, Math.max(8.98, stepThreeTitleEnd + 0.10), 2.77, 0.16);

  // Official, unmodified store badges. Apple's visible badge is printed at
  // 0.40 in (10.16 mm), satisfying its 10 mm print minimum. Google's source
  // PNG includes the prescribed quarter-badge clear space, so its full image
  // is taller while the visible black badge matches Apple's 0.40 in height.
  if (appStoreUrl && appStoreBadgeDataUrl) {
    drawStoreBadge(doc, appStoreBadgeDataUrl, appStoreUrl, 1.02, 9.48, 1.20, 0.40);
  }
  if (googlePlayUrl && googlePlayBadgeDataUrl) {
    drawStoreBadge(
      doc,
      googlePlayBadgeDataUrl,
      googlePlayUrl,
      appStoreUrl && appStoreBadgeDataUrl ? 2.36 : 0.92,
      9.38,
      1.55,
      0.60
    );
  }

  // Primary conversion panel, right.
  const panelX = 5.05;
  const panelY = 5.30;
  const panelW = 3.07;
  const panelH = 4.46;
  doc.setFillColor(COLORS.greenLight);
  doc.roundedRect(panelX, panelY, panelW, panelH, 0.12, 0.12, 'F');
  setText(doc, 15.5, COLORS.slateDeep, 'bolditalic');
  doc.text('Connect to', panelX + 0.35, panelY + 0.41);
  setText(doc, 18, COLORS.slateDeep, 'bold');
  let connectName = doc.splitTextToSize(clinicName, panelW - 0.70);
  if (connectName.length > 2) {
    setText(doc, 14, COLORS.slateDeep, 'bold');
    connectName = doc.splitTextToSize(clinicName, panelW - 0.70);
  }
  if (connectName.length > 3) {
    setText(doc, 11.5, COLORS.slateDeep, 'bold');
    connectName = doc.splitTextToSize(clinicName, panelW - 0.70);
  }
  doc.text(connectName, panelX + 0.35, panelY + 0.69);

  // Preserve the QR, explanatory copy, and manual code inside the fixed
  // one-page panel even when a legitimate clinic name wraps to four lines.
  // Typical names keep the larger QR; exceptionally long names use a still
  // comfortably scannable 1.25-inch code and slightly tighter body copy.
  const compactConnectionPanel = connectName.length > 3;
  const qrSize = compactConnectionPanel ? 1.25 : 1.68;
  const qrY = panelY + (compactConnectionPanel ? 0.79 : 0.82) + connectName.length * 0.18;
  const qrX = panelX + (panelW - qrSize) / 2;
  doc.setFillColor(COLORS.white);
  doc.roundedRect(qrX - 0.10, qrY - 0.10, qrSize + 0.20, qrSize + 0.20, 0.09, 0.09, 'F');
  doc.addImage(enrollmentQrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

  setText(doc, compactConnectionPanel ? 8.3 : 9.3, COLORS.slate, 'normal');
  const instructionY = qrY + qrSize + (compactConnectionPanel ? 0.22 : 0.31);
  wrapped(
    doc,
    'Open Cairn and scan this code to request a connection with your clinic. Your clinic will review the request before assigning any recommended monitoring.',
    panelX + 0.35,
    instructionY,
    panelW - 0.70,
    compactConnectionPanel ? 0.135 : 0.155
  );

  if (enrollmentCode) {
    // Anchor the manual fallback within the panel. The responsive sizing
    // above ensures the preceding copy ends before this reserved region.
    const codeBoxY = panelY + panelH - 0.74;
    doc.setFillColor(COLORS.sandstoneLight);
    doc.roundedRect(panelX + 0.28, codeBoxY, panelW - 0.56, 0.61, 0.08, 0.08, 'F');
    setText(doc, 8.6, COLORS.slateDeep, 'bold');
    doc.text('C L I N I C   C O D E', panelX + panelW / 2, codeBoxY + 0.20, { align: 'center' });
    doc.setFont('courier', 'bold');
    doc.setFontSize(19);
    doc.text(enrollmentCode, panelX + panelW / 2, codeBoxY + 0.49, { align: 'center' });
  }

  // Quiet safety footer.
  const footerY = 9.98;
  doc.setFillColor(COLORS.fog);
  doc.roundedRect(MARGIN, footerY, CONTENT_WIDTH, 0.73, 0.10, 0.10, 'F');
  if (markDataUrl) doc.addImage(markDataUrl, 'PNG', MARGIN + 0.22, footerY + 0.13, 0.45, 0.45);
  doc.setDrawColor(COLORS.moss);
  doc.setLineWidth(0.012);
  doc.line(MARGIN + 0.88, footerY + 0.14, MARGIN + 0.88, footerY + 0.59);
  setText(doc, 10.8, COLORS.slateDeep, 'bold');
  doc.text('Cairn supports — not replaces — veterinary care.', MARGIN + 1.12, footerY + 0.29);
  setText(doc, 8.4, COLORS.slate, 'normal');
  wrapped(
    doc,
    'Information in this app is not monitored continuously. If you are concerned about your pet, contact your veterinary clinic or an emergency veterinary hospital directly.',
    MARGIN + 1.12,
    footerY + 0.50,
    CONTENT_WIDTH - 1.35,
    0.13
  );

  setText(doc, 7.6, COLORS.textSoft, 'normal');
  const websiteLabel = 'cairnpethealth.com';
  const websiteX = (PAGE_WIDTH - doc.getTextWidth(websiteLabel)) / 2;
  doc.textWithLink(websiteLabel, websiteX, PAGE_HEIGHT - 0.12, { url: 'https://cairnpethealth.com' });

  return doc;
}

export function downloadPatientMonitoringGuide(doc, clinicName) {
  doc.save(sanitizeFilename(clinicName));
}
